import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ModalDismissReasons, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { BannerPopupSlide, flattenBannerSlides, newBannerImpressionId } from 'src/app/components/banner-popup/banner-popup.component';
import { BuildReportService } from 'src/app/services/build-report/build-report.service';
import { CommonService } from 'src/app/services/common/common.service';
import { DataPocketService } from 'src/app/services/data-pocket/data-pocket.service';
import { DataService } from 'src/app/services/data/data.service';
import { VersionService } from 'src/app/services/version/version.service';
import { AppRoute } from 'src/app/shared/constants/app-route.constant';
import { PHOTOBOOTH_MODAL_OPTIONS } from 'src/app/shared/constants/modal-options.constant';
import {
  AppConfig,
  BannerCloseReason,
  BannerOrientation,
  BannerPopupResult,
  GlobalConfigKey,
  HiddenMenuMode,
  InterfaceText,
  PinDialogVerifyResult,
  StorageKey,
} from 'src/app/shared/constants/global-config.constant';
import { OmegaRandom } from 'src/app/shared/constants/omega-random.constat';
import { IBannerCampaign, IRequestBannerEvent, IResponseBranchUserInterface } from 'src/app/shared/interfaces/interface';
@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss']
})
export class HomeComponent implements OnInit, OnDestroy {

  constructor(
    private router: Router,
    private ngbModal: NgbModal,
    public commonService: CommonService,
    private dataService: DataService,
    private dataPocketService: DataPocketService,
    private versionService: VersionService,
    private activatedRoute: ActivatedRoute,
    private buildReportService: BuildReportService,
  ) { }

  version = this.versionService.version;
  branchInterface: IResponseBranchUserInterface = null;

  isInterfaceError = false;
  isOffline: boolean = false;

  typed: string = '';
  modalRef: NgbModalRef;
  currentHiddenMenuMode: HiddenMenuMode = HiddenMenuMode.NONE;

  TXT_HOME_PIN_DIALOG_TITLE = InterfaceText.HOME_PIN_TITLE;
  TXT_HOME_PIN_DIALOG_HEADER = InterfaceText.HOME_PIN_HEADER;
  TXT_PAYMENT_OPTION_DIALOG_DEL_BUTTON = InterfaceText.PAYMENT_OPTION_DIALOG_DEL_BUTTON;

  countTimer: any;
  countValue = 0;
  @ViewChild('hiddenMenu') hiddenMenu: any;

  // ---- Banner popup (promotional campaigns from the back office) ----
  @ViewChild('bannerPopup') bannerPopup: any;
  bannerCampaigns: IBannerCampaign[] = [];
  bannerOrientation: BannerOrientation = 'landscape';
  bannerSlides: BannerPopupSlide[] = [];
  bannerModalRef: NgbModalRef | null = null;
  private bannerImpressionId = '';
  private bannerClosedSent = false;
  /** Set once this visit must not open the popup any more (left /home, went offline). */
  private bannerCancelled = false;
  private bannerPreloadTimer: any = null;
  private bannerPreloadImage: HTMLImageElement | null = null;

  async ngOnInit() {
    if (!this.commonService.checkSessionExisted()) {
      this.router.navigate([AppRoute.VERIFICATION]);
    }
    else {
      this.activatedRoute.queryParams.subscribe(params => {
        if (params.show_config !== undefined && params.show_config === 'true') {
          this.router.navigate([AppRoute.CONFIG]);
        }
      });

      if (this.commonService.checkIfConfigNeedReload()) {
        await this.dataPocketService.loginPocketbase();
        await this.dataPocketService.initiateConfigFromPocketbase();
      }

      this.getBranchInterface();
      // Staff shortcuts into the config / hidden menu never get the advertisement.
      const query = this.activatedRoute.snapshot?.queryParams ?? {};
      const staffShortcut = query.show_config === 'true' || query.show_hidden === 'true';
      this.loadBannerPopup(staffShortcut);
      // Which build this booth runs, for GET /application/branch/seen. Background, silent.
      this.buildReportService.report();
      this.commonService.clearPinUse();
      this.commonService.clearTrxId();
      this.downloadBranchId();
      this.downloadBranchCode();
      this.downloadBranchSession();
    }
  }

  ngOnDestroy() {
    // Leaving /home (PIN success, logout, terms...) takes the popup with it.
    this.bannerCancelled = true;
    this.clearBannerPreload();
    if (this.bannerModalRef) {
      this.sendBannerClosed(BannerCloseReason.NAVIGATED);
      this.bannerModalRef.close();
      this.bannerModalRef = null;
    }
  }

  /**
   * Asks the API for the campaigns live on this branch and opens the popup once the first
   * picture has fully loaded - never earlier, so the customer never sees a blank or
   * half-drawn popup, and never if that takes longer than BANNER_FIRST_SLIDE_TIMEOUT_MS.
   * Every failure is silent: no toast, no loading overlay, no popup.
   */
  async loadBannerPopup(skip: boolean) {
    if (skip || this.isOffline) {
      return;
    }
    let campaigns: IBannerCampaign[] = [];
    try {
      const response = await this.dataService.getActiveBanners(localStorage.getItem(StorageKey.BRANCH_ID)).toPromise();
      campaigns = response?.data ?? [];
    }
    catch (err) {
      return;
    }
    if (this.bannerCancelled || campaigns.length === 0) {
      return;
    }

    const orientation: BannerOrientation = window.innerHeight > window.innerWidth ? 'portrait' : 'landscape';
    const slides = flattenBannerSlides(campaigns, orientation);
    if (slides.length === 0) {
      return;
    }

    const ready = await this.preloadBannerImage(slides[0].url);
    if (!ready || this.bannerCancelled || this.isOffline || !this.isStillOnHome()) {
      return;
    }
    this.bannerCampaigns = campaigns;
    this.bannerOrientation = orientation;
    this.bannerSlides = slides;
    this.openBannerPopup();
  }

  /** Overridable so tests can hand back a fake image. */
  protected createImage(): HTMLImageElement {
    return new Image();
  }

  private preloadBannerImage(url: string): Promise<boolean> {
    return new Promise<boolean>(resolve => {
      let settled = false;
      const finish = (ok: boolean) => {
        if (settled) {
          return;
        }
        settled = true;
        this.clearBannerPreload();
        resolve(ok);
      };
      try {
        const img = this.createImage();
        this.bannerPreloadImage = img;
        img.onload = () => finish(true);
        img.onerror = () => finish(false);
        this.bannerPreloadTimer = setTimeout(() => finish(false), AppConfig.BANNER_FIRST_SLIDE_TIMEOUT_MS);
        img.src = url;
      }
      catch (err) {
        finish(false);
      }
    });
  }

  private clearBannerPreload() {
    if (this.bannerPreloadTimer) {
      clearTimeout(this.bannerPreloadTimer);
      this.bannerPreloadTimer = null;
    }
    if (this.bannerPreloadImage) {
      this.bannerPreloadImage.onload = null;
      this.bannerPreloadImage.onerror = null;
      this.bannerPreloadImage = null;
    }
  }

  private isStillOnHome(): boolean {
    const path = (this.router.url || '').split('?')[0];
    return path === '/' || path === '/' + AppRoute.HOME;
  }

  private openBannerPopup() {
    this.bannerImpressionId = newBannerImpressionId();
    this.bannerClosedSent = false;
    this.bannerModalRef = this.ngbModal.open(this.bannerPopup, {
      centered: true,
      backdrop: true,
      keyboard: false,
      windowClass: 'banner-modal',
      backdropClass: 'banner-backdrop',
    });
    // ngbModal.dismissAll() (hidden menu) and a backdrop tap both end up here; close() does not.
    this.bannerModalRef.result.then(
      () => { },
      reason => {
        this.sendBannerClosed(reason === ModalDismissReasons.BACKDROP_CLICK
          ? BannerCloseReason.BACKDROP
          : BannerCloseReason.NAVIGATED);
        this.bannerModalRef = null;
      },
    );

    const seen = new Set<number>();
    for (const slide of this.bannerSlides) {
      if (!seen.has(slide.campaign_id)) {
        seen.add(slide.campaign_id);
        this.sendBannerEvent({ campaign_id: slide.campaign_id, event: 'shown' });
      }
    }
    this.onBannerSlideView(0);
  }

  onBannerSlideView(index: number) {
    const slide = this.bannerSlides[index];
    if (slide) {
      this.sendBannerEvent({ campaign_id: slide.campaign_id, event: 'slide_view', slide_index: slide.slide_index });
    }
  }

  onBannerClose(result: BannerPopupResult) {
    this.sendBannerClosed(result?.reason ?? BannerCloseReason.BUTTON);
    if (this.bannerModalRef) {
      this.bannerModalRef.close();
      this.bannerModalRef = null;
    }
  }

  /** Exactly one closed event per opening, whichever path closes it first. */
  private sendBannerClosed(reason: BannerCloseReason) {
    if (this.bannerClosedSent) {
      return;
    }
    this.bannerClosedSent = true;
    const seen = new Set<number>();
    for (const slide of this.bannerSlides) {
      if (!seen.has(slide.campaign_id)) {
        seen.add(slide.campaign_id);
        this.sendBannerEvent({ campaign_id: slide.campaign_id, event: 'closed', close_reason: reason });
      }
    }
  }

  private sendBannerEvent(partial: Pick<IRequestBannerEvent, 'campaign_id' | 'event'> & Partial<IRequestBannerEvent>) {
    const payload: IRequestBannerEvent = {
      impression_id: this.bannerImpressionId,
      branch_id: Number(localStorage.getItem(StorageKey.BRANCH_ID)),
      kiosk_time: new Date().toISOString(),
      ...partial,
    };
    try {
      this.dataService.sendBannerEvent(payload).subscribe({ next: () => { }, error: () => { } });
    }
    catch (err) {
      // Fire-and-forget: a lost event changes nothing for the customer.
    }
  }

  ngAfterViewInit() {
    this.activatedRoute.queryParams.subscribe(params => {
      if (params.show_hidden !== undefined && params.show_hidden === 'true') {
        this.openFullscreen();
      }
    });

  }

  goToPaymentOption() {
    this.router.navigate([AppRoute.PAYMENT_OPTION]);
  }

  increaseCount() {
    this.countValue += 1;
    console.log(this.countValue);
  }

  checkAndClearCountValue() {
    console.log('Tap count: ' + this.countValue);

    if (this.countValue === 0) {
      this.goToNext();
    }
    else if (this.countValue >= AppConfig.HIDDEN_MENU_COUNT) {
      this.openFullscreen();
    }
    this.countValue = 0;
  }

  goToNext() {
    this.router.navigate([AppRoute.TERMS]);
  }

  openFullscreen() {
    console.log(this.hiddenMenu)
    this.ngbModal.open(this.hiddenMenu, { ...PHOTOBOOTH_MODAL_OPTIONS, size: 'lg' });
  }

  goToConfig() {
    this.currentHiddenMenuMode = HiddenMenuMode.CONFIG;
    this.ngbModal.dismissAll();
    this.openPin();
  }

  goToExit() {
    this.currentHiddenMenuMode = HiddenMenuMode.EXIT;
    this.ngbModal.dismissAll();
    this.openPin();
  }

  goToLogout() {
    this.currentHiddenMenuMode = HiddenMenuMode.LOGOUT;
    this.ngbModal.dismissAll();
    this.openPin();
  }

  async getBranchInterface() {
    this.commonService.isLoading = true;
    try {

      await this.checkUseOmegaRandom();
      let tempBranchInterface = await this.dataService.getBranchInterfaceById(localStorage.getItem(StorageKey.BRANCH_ID)).toPromise();

      if (sessionStorage.getItem(StorageKey.USE_OMEGA_RANDOM) === 'true') {
        tempBranchInterface.home_background_image_url = '';
        tempBranchInterface.ready_background_image_url = '';
        this.branchInterface = tempBranchInterface;
        this.initOmegaRandom();
      }
      else {
        this.branchInterface = tempBranchInterface;
      }

      localStorage.setItem(StorageKey.BRANCH_INTERFACE, JSON.stringify(this.branchInterface));
      this.preloadReadyImage(this.branchInterface?.ready_background_image_url);
    }
    catch (err) {
      this.isInterfaceError = true;
    }
    finally {
      this.commonService.isLoading = false;
    }
  }

  /**
   * Fetches the payment-success picture now, while the customer is still choosing, so it
   * is already in the browser cache when they have paid. The files are served with a
   * 30-day max-age, so after the first visit this costs nothing. Deferred and low
   * priority so it never competes with this page's own pictures.
   */
  private preloadReadyImage(url: string | null | undefined) {
    if (!url) {
      return;
    }
    setTimeout(() => {
      try {
        const img = new Image();
        (img as any).fetchPriority = 'low';
        img.decoding = 'async';
        img.src = url;
      }
      catch (err) {
        // A failed preload changes nothing: payment-success fetches it again anyway.
      }
    }, 2000);
  }

  @ViewChild('content') content: any;
  /** The staff PIN dialog in staff-menu mode: POST /staff-menu with the chosen action (D27). */
  openPin() {
    this.modalRef = this.ngbModal.open(this.content, PHOTOBOOTH_MODAL_OPTIONS);
  }

  handleCloseDialog(data: PinDialogVerifyResult) {
    console.log('Close dialog')
    this.modalRef.dismiss();
  }

  handleSuccessVerify(data: PinDialogVerifyResult) {
    console.log('Success verify')
    if (data.mode === HiddenMenuMode.CONFIG) {
      this.modalRef.close();
      this.router.navigate([AppRoute.CONFIG]);
    }
    else if (data.mode === HiddenMenuMode.EXIT) {
      this.modalRef.close();
      this.router.navigate([AppRoute.EXIT]);
    }
    else if (data.mode === HiddenMenuMode.LOGOUT) {
      this.modalRef.close();
      this.doLogout();
    }
  }

  doLogout() {
    this.commonService.isLoading = true;
    localStorage.removeItem(StorageKey.SESSION_ID);
    localStorage.removeItem(StorageKey.BRANCH_CODE);
    localStorage.removeItem(StorageKey.BRANCH_ID);
    localStorage.removeItem(StorageKey.POCKET_KEY);
    localStorage.removeItem(StorageKey.POCKETBASE_AUTH);
    localStorage.removeItem(StorageKey.POCKET_TOKEN);
    localStorage.removeItem(StorageKey.PROMO_ID);
    localStorage.removeItem(StorageKey.PROMO_TYPE);
    localStorage.removeItem(StorageKey.PROMO_CODE);
    localStorage.removeItem(StorageKey.PROMO_AMOUNT);
    localStorage.removeItem(StorageKey.PROMO_USE);
    localStorage.removeItem(StorageKey.BRANCH_INTERFACE)
    localStorage.removeItem(StorageKey.IS_PIN_USE);
    sessionStorage.removeItem(StorageKey.USE_OMEGA_RANDOM);
    sessionStorage.removeItem(StorageKey.USE_OMEGA_RANDOM_READY_CLICK);
    this.router.navigate([AppRoute.VERIFICATION]);
    this.commonService.isLoading = false;
  }

    downloadBranchId() {
    const branchId = localStorage.getItem(StorageKey.BRANCH_ID) ?? '';
    const blob = new Blob([branchId], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'branchId.txt';
    anchor.click();
    URL.revokeObjectURL(url);
  }

  downloadBranchCode() {
    const branchCode = localStorage.getItem(StorageKey.BRANCH_CODE) ?? '';
    const blob = new Blob([branchCode], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'branchCode.txt';
    anchor.click();
    URL.revokeObjectURL(url);
  }

  downloadBranchSession() {
    const branchSession = localStorage.getItem(StorageKey.SESSION_ID) ?? '';
    const blob = new Blob([branchSession], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'branchSession.txt';
    anchor.click();
    URL.revokeObjectURL(url);
  }

  setOnline() {
    this.isOffline = false;
  }

  setOffline() {
    this.isOffline = true;
    // A Bootstrap modal (z-index 1050) would sit on top of the offline screen (999).
    this.bannerCancelled = true;
    this.clearBannerPreload();
    if (this.bannerModalRef) {
      this.sendBannerClosed(BannerCloseReason.OFFLINE);
      this.bannerModalRef.close();
      this.bannerModalRef = null;
    }
  }

  async checkUseOmegaRandom() {
    console.log('Check use omega random');
    const configResult = this.commonService.getConfigUseOmegaRandom();
    if (configResult) {
      if (configResult.length > 0) {
        if (configResult[0].includes(localStorage.getItem(StorageKey.BRANCH_CODE))) {
          sessionStorage.setItem(StorageKey.USE_OMEGA_RANDOM, 'true');
        }
      }
      console.log(configResult);
    }
  }

  initOmegaRandom() {
    this.commonService.isLoading = true;
    try {
      const OmegeHomeBackgroundList: string[] = OmegaRandom.HOME
      const randomIndex = Math.floor(Math.random() * OmegeHomeBackgroundList.length);
      this.branchInterface.home_background_image_url = OmegeHomeBackgroundList[randomIndex];

      const OmegeStartBackgroundList: string[] = OmegaRandom.START
      const randomIndex2 = Math.floor(Math.random() * OmegeStartBackgroundList.length);
      this.branchInterface.ready_background_image_url = OmegeStartBackgroundList[randomIndex2];

      console.log('Use Omega Random Home Background: ' + this.branchInterface.home_background_image_url);
      console.log('Use Omega Random Ready Background: ' + this.branchInterface.ready_background_image_url);

      sessionStorage.setItem(StorageKey.USE_OMEGA_RANDOM_READY_CLICK, 'false');

    }
    catch (err) {
      console.log(err);
      this.isInterfaceError = true;
    }
    finally {
      this.commonService.isLoading = false;
    }
  }

}
